import React, { useState } from 'react';
import { useTheme } from '../hooks/useTheme';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';

const ShareModal = ({ isOpen, onClose, diagramId, diagramName, diagramType }) => {
    const { theme } = useTheme();
    const [accessMode, setAccessMode] = useState('view');
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const shareUrl = diagramId
        ? `${window.location.origin}/editor/${diagramId}${diagramType ? `?type=${diagramType}` : ''}`
        : null;

    const handleCopy = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // fallback for non-secure contexts
            const el = document.createElement('textarea');
            el.value = shareUrl;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const inputStyle = {
        flex: 1,
        padding: '8px 12px',
        border: `1px solid ${theme.toolbar.border}`,
        borderRadius: '6px 0 0 6px',
        background: theme.name === 'light' ? '#f8fafc' : '#1e2328',
        color: theme.toolbar.text,
        fontSize: 13,
        outline: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    };

    const radioLabelStyle = (value) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        border: `1px solid ${accessMode === value ? '#3b82f6' : theme.toolbar.border}`,
        background: accessMode === value
            ? (theme.name === 'light' ? '#eff6ff' : '#1e3a5f')
            : theme.toolbar.buttonBackground,
        color: theme.toolbar.text,
        fontSize: 13,
        transition: 'all 0.15s ease',
        userSelect: 'none',
    });

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 4000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: theme.toolbar.background,
                    border: `1px solid ${theme.toolbar.border}`,
                    borderRadius: 12,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    width: 420,
                    maxWidth: 'calc(100vw - 32px)',
                    padding: '24px',
                    position: 'relative',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: theme.toolbar.text }}>
                            Share diagram
                        </div>
                        {diagramName && (
                            <div style={{ fontSize: 12, color: theme.toolbar.text, opacity: 0.6, marginTop: 2 }}>
                                {diagramName}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: theme.toolbar.text,
                            opacity: 0.5,
                            padding: 4,
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: 4,
                        }}
                    >
                        <CloseIcon style={{ fontSize: 20 }} />
                    </button>
                </div>

                {/* Link section */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.toolbar.text, opacity: 0.7, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Shareable link
                    </div>
                    {shareUrl ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                                style={inputStyle}
                                value={shareUrl}
                                readOnly
                                onFocus={(e) => e.target.select()}
                            />
                            <button
                                onClick={handleCopy}
                                title={copied ? 'Copied!' : 'Copy link'}
                                style={{
                                    padding: '8px 12px',
                                    border: `1px solid ${theme.toolbar.border}`,
                                    borderLeft: 'none',
                                    borderRadius: '0 6px 6px 0',
                                    background: copied ? '#22c55e' : '#3b82f6',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 13,
                                    transition: 'background 0.2s',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                {copied
                                    ? <><CheckIcon style={{ fontSize: 16 }} /> Copied</>
                                    : <><ContentCopyIcon style={{ fontSize: 16 }} /> Copy</>
                                }
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            padding: '10px 12px',
                            borderRadius: 6,
                            background: theme.name === 'light' ? '#fef3c7' : '#3d2e0a',
                            border: `1px solid ${theme.name === 'light' ? '#fcd34d' : '#78350f'}`,
                            color: theme.name === 'light' ? '#92400e' : '#fbbf24',
                            fontSize: 13,
                        }}>
                            Save the diagram first to generate a shareable link.
                        </div>
                    )}

                    {shareUrl && (
                        <a
                            href={shareUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                marginTop: 8,
                                fontSize: 12,
                                color: '#3b82f6',
                                textDecoration: 'none',
                            }}
                        >
                            <OpenInNewIcon style={{ fontSize: 14 }} />
                            Open in new tab
                        </a>
                    )}
                </div>

                {/* Access mode */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.toolbar.text, opacity: 0.7, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Access
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <label style={radioLabelStyle('view')}>
                            <input
                                type="radio"
                                name="accessMode"
                                value="view"
                                checked={accessMode === 'view'}
                                onChange={() => setAccessMode('view')}
                                style={{ accentColor: '#3b82f6' }}
                            />
                            <div>
                                <div style={{ fontWeight: 500 }}>View only</div>
                                <div style={{ fontSize: 11, opacity: 0.6 }}>Anyone with the link can view</div>
                            </div>
                        </label>
                        <label style={radioLabelStyle('edit')}>
                            <input
                                type="radio"
                                name="accessMode"
                                value="edit"
                                checked={accessMode === 'edit'}
                                onChange={() => setAccessMode('edit')}
                                style={{ accentColor: '#3b82f6' }}
                            />
                            <div>
                                <div style={{ fontWeight: 500 }}>Can edit</div>
                                <div style={{ fontSize: 11, opacity: 0.6 }}>Anyone with the link can edit</div>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Invite by email — coming soon */}
                <div style={{
                    borderTop: `1px solid ${theme.toolbar.border}`,
                    paddingTop: 16,
                    opacity: 0.4,
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.toolbar.text, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Invite by email
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 400, background: theme.name === 'light' ? '#e2e8f0' : '#374151', padding: '2px 6px', borderRadius: 4 }}>
                            coming soon
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            style={{ ...inputStyle, borderRadius: 6, flex: 1 }}
                            placeholder="colleague@company.com"
                            disabled
                        />
                        <button
                            disabled
                            style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: `1px solid ${theme.toolbar.border}`,
                                background: theme.toolbar.buttonBackground,
                                color: theme.toolbar.text,
                                fontSize: 13,
                                cursor: 'not-allowed',
                            }}
                        >
                            Invite
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
